import { User } from '../types/user.js';
import { Video, VideoJob, VideoMp4s, Mp4Preset } from '../types/video.js';
import { Comment } from '../types/comment.js';
import { Channel, ChannelVisibility } from '../types/channel.js';
import { authFetch, authUpload, type UploadOptions } from './authFetch';

// Define the base URL for the API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://nodebackendforacvrecording-1.onrender.com';

// Define response types
interface ApiResponse<T> {
  data: T | null;
  error: Error | null;
}

/** Viewer insights of one video (owner only; see backend viewInsightsService). */
export interface VideoInsights {
  since: string | null;
  totals: { views: number; uniqueViewers: number; averagePercent: number; completed: number };
  /** Viewers per band of how far they got: <25 %, 25–49 %, 50–74 %, 75 %+. */
  distribution: [number, number, number, number];
  viewers: { email: string | null; name: string | null; lastWatchedAt: string; percent: number; views: number }[];
  anonymous: { count: number; averagePercent: number };
}

/** One entry of the deletion history (see backend deletionHistoryService). */
export interface DeletionHistoryEntry {
  id: string;
  videoId: string;
  videoTitle: string;
  ownerEmail: string;
  action: string;
  actorEmail: string;
  actorRole: string | null;
  createdAt: string;
  purgeAt: string | null;
  details: {
    video?: { fileSize?: number; mimeType?: string | null; duration?: number; source?: string; createdAt?: string | null };
    files?: { video?: boolean; thumbnail?: boolean; mp4Copies?: { preset: string; size: number }[] };
    storageFreedBytes?: number;
    comments?: number;
    commentImages?: number;
    reactions?: number;
    channel?: { id: string; name: string | null } | null;
    playlists?: { id: string; title: string | null; position: number }[];
    share?: { wasShared: boolean; visibility: string };
    notRemoved?: string[];
  } | null;
}

export type DeletionHistoryFilter = 'all' | 'moved_to_bin' | 'restored' | 'deleted_permanently';

export interface DeletionHistoryQuery {
  page: number;
  pageSize: number;
  q?: string;
  filter: DeletionHistoryFilter;
  /** Only the last N days (0 = the whole history). */
  days: number;
}

export interface DeletionHistoryPage {
  items: DeletionHistoryEntry[];
  /** Entries matching the search, filter and period (all pages). */
  total: number;
  /** Entries per filter under the same search and period. */
  counts: Record<DeletionHistoryFilter, number>;
  page: number;
  pageSize: number;
}

export interface SubscriptionResult {
  subscribed: boolean;
  subscriberCount: number;
}

export interface ChannelSubscriber {
  subscriberEmail: string;
  subscriberName: string | null;
  subscribedAt: string;
}

interface ReactionCounts {
  like: number;
  dislike: number;
  user_reaction: 'like' | 'dislike' | null;
}

// Helper function to add user authentication to requests
const addUserAuth = (url: URL, userEmail: string | null, slug?: string): URL => {
  if (userEmail) {
    url.searchParams.append('userEmail', userEmail);
  }
  if (slug) {
    url.searchParams.append('slug', slug);
  }
  console.log(`[ApiClient] Adding auth params to URL: userEmail=${userEmail}, slug=${slug}`);
  return url;
};

// Anonymous share-page viewers get a random id stored in this browser. It is kept under its
// own key and is never used as the viewer's email/identity.
const ANON_VIEWER_ID_KEY = 'airec_anon_viewer_id';
const getAnonViewerId = (): string => {
  let id = localStorage.getItem(ANON_VIEWER_ID_KEY);
  if (!id) {
    id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(ANON_VIEWER_ID_KEY, id);
  }
  return id;
};

// Helper function to handle API responses
const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  if (!response.ok) {
    const errorText = await response.text();
    // JSON errors ({ code, message } / { error }) become a readable message; `code` and
    // `status` stay available to callers that react to specific failures.
    let message = errorText;
    let code: string | undefined;
    let body: any;
    try {
      body = JSON.parse(errorText);
      message = body?.message || body?.error || errorText;
      code = typeof body?.code === 'string' ? body.code : undefined;
    } catch { /* plain-text error */ }
    const error = Object.assign(new Error(message || `API error: ${response.status}`), {
      code,
      status: response.status,
      body,
    });
    return { data: null, error };
  }

  // 204 No Content — successful but no body to parse
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return { data: null, error: null };
  }

  try {
    const data = await response.json();
    return {
      data,
      error: null
    };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Unknown error')
    };
  }
};

// API Client class
export class ApiClient {
  private userEmail: string | null = null;
  private userId: string | null = null;
  private slug: string | null = null;
  private authorName: string | null = null;

  // Auth methods
  async signIn(email: string, slug?: string): Promise<ApiResponse<User>> {
    this.userEmail = email;
    this.slug = slug || null;
    
    const url = new URL(`${API_BASE_URL}/api/auth/signin`);
    addUserAuth(url, email, slug);
    
    const response = await authFetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    const result = await handleResponse<{ user: User }>(response);
    
    if (result.data) {
      // Store user info in local storage
      localStorage.setItem('user', JSON.stringify(result.data.user));
      this.userId = result.data.user?.id || null;
      return {
        data: result.data.user,
        error: null
      };
    }
    
    return {
      data: null,
      error: result.error
    };
  }

  async signOut(): Promise<ApiResponse<null>> {
    const response = await authFetch(`${API_BASE_URL}/api/auth/signout`, {
      method: 'POST'
    });
    
    // Clear local storage
    localStorage.removeItem('user');
    this.userEmail = null;
    this.slug = null;
    
    return handleResponse<null>(response);
  }

  async getSession(): Promise<ApiResponse<User>> {
    // Check local storage first
    const storedUser = localStorage.getItem('user');
    
    // Try to get email and slug from localStorage if not set in the client
    if (!this.userEmail) {
      const storedEmail = localStorage.getItem('userEmail');
      if (storedEmail) {
        console.log('[ApiClient] Setting email from localStorage:', storedEmail);
        this.userEmail = storedEmail;
      }
    }
    
    if (!this.slug) {
      const storedSlug = localStorage.getItem('slug');
      if (storedSlug) {
        console.log('[ApiClient] Setting slug from localStorage:', storedSlug);
        this.slug = storedSlug;
      }
    }
    
    // Use stored user only if it belongs to the current email — a cached user from a
    // previous session in this browser must never be returned for someone else.
    let cachedUser: (User & { mail?: string }) | null = null;
    try {
      cachedUser = storedUser ? JSON.parse(storedUser) : null;
    } catch (e) {
      console.error('[ApiClient] Invalid stored user:', e);
    }
    const cachedEmail = String(cachedUser?.email || cachedUser?.mail || '').toLowerCase();
    if (cachedUser && this.userEmail && cachedEmail !== this.userEmail.toLowerCase()) {
      console.warn('[ApiClient] Cached user belongs to a different email, ignoring it');
      localStorage.removeItem('user');
      cachedUser = null;
    }

    if (cachedUser && this.userEmail) {
      try {
        const user = cachedUser;
        console.log('[ApiClient] Using stored user from localStorage:', { email: user.email, slug: this.slug });
        
        // Make sure we have the latest slug
        if (this.slug && (!user.slug || user.slug !== this.slug)) {
          console.log('[ApiClient] Updating stored user with current slug:', this.slug);
          user.slug = this.slug;
          localStorage.setItem('user', JSON.stringify(user));
        }
        
        return {
          data: user,
          error: null
        };
      } catch (e) {
        console.error('[ApiClient] Invalid stored user:', e);
        // Invalid stored user, continue with API call
      }
    }
    
    if (!this.userEmail) {
      console.error('[ApiClient] No user email available for session check');
      return {
        data: null,
        error: new Error('No user email available')
      };
    }
    
    const url = new URL(`${API_BASE_URL}/api/auth/session`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    console.log('[ApiClient] Requesting session from URL:', url.toString());
    
    try {
      const response = await authFetch(url.toString());
      console.log('[ApiClient] Session response status:', response.status);
      const result = await handleResponse<{ user: User | null }>(response);
      console.log('[ApiClient] Session result:', result);
    
      if (result.data && result.data.user) {
        // Store the updated user in localStorage
        localStorage.setItem('user', JSON.stringify(result.data.user));
        
        // Make sure we store the email and slug separately too for redundancy
        if (result.data.user.email) {
          localStorage.setItem('userEmail', result.data.user.email);
        }
        
        if (result.data.user.slug) {
          localStorage.setItem('slug', result.data.user.slug);
        }
        
        console.log('[ApiClient] Session successful, got user:', result.data.user);
        return {
          data: result.data.user,
          error: null
        };
      }
      
      console.error('[ApiClient] Session check failed, no user data in response');
      return {
        data: null,
        error: new Error('Session check failed')
      };
    } catch (error) {
      console.error('[ApiClient] Error checking session:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error checking session')
      };
    }
  }

  // Video methods
  async listVideos(): Promise<ApiResponse<Video[]>> {
    if (!this.userEmail) {
      return {
        data: null,
        error: new Error('User not authenticated')
      };
    }
    
    const url = new URL(`${API_BASE_URL}/api/videos`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    
    const response = await authFetch(url.toString());
    return handleResponse<Video[]>(response);
  }

  /** `options`: upload progress and cancel (an AbortError rejects the call). */
  async uploadVideo(file: File, title: string, description: string, durationSeconds?: number, options?: UploadOptions): Promise<ApiResponse<Video>> {
    if (!this.userEmail) {
      return {
        data: null,
        error: new Error('User not authenticated')
      };
    }
    
    const url = new URL(`${API_BASE_URL}/api/videos/upload`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    
    const formData = new FormData();
    formData.append('video', file);
    formData.append('title', title);
    formData.append('description', description);
    if (durationSeconds != null && durationSeconds > 0) {
      formData.append('duration', String(Math.round(durationSeconds)));
    }
    if (this.slug) {
      formData.append('slug', this.slug);
    }
    
    const response = options
      ? await authUpload(url.toString(), formData, options)
      : await authFetch(url.toString(), {
        method: 'POST',
        body: formData
      });
    
    return handleResponse<Video>(response);
  }

  /** Moves the video to the Recycle Bin (restorable until `purgeAt`). */
  async deleteVideo(videoId: string): Promise<ApiResponse<{ purgeAt: string }>> {
    if (!this.userEmail) {
      return {
        data: null,
        error: new Error('User not authenticated')
      };
    }
    
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    
    const response = await authFetch(url.toString(), {
      method: 'DELETE'
    });
    
    return handleResponse<{ purgeAt: string }>(response);
  }

  /** Recycle Bin requests: GET list, POST restore, DELETE permanently / empty. */
  private async binRequest<T>(path: string, method = 'GET'): Promise<ApiResponse<T>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/${path}`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    return handleResponse<T>(await authFetch(url.toString(), { method }));
  }

  listRecycleBin() { return this.binRequest<Video[]>('bin'); }

  /** The school's Admin Center Archive Mode state for this user (src/hooks/useArchiveAccess.ts). */
  async getArchiveAccess(): Promise<ApiResponse<{ mode: 'full' | 'readOnly' | 'blocked'; status: string | null; legalHold: boolean }>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/archive/access`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    return handleResponse(await authFetch(url.toString()));
  }
  restoreVideo(videoId: string) { return this.binRequest<Video>(`${encodeURIComponent(videoId)}/restore`, 'POST'); }
  deleteVideoPermanently(videoId: string) { return this.binRequest<null>(`${encodeURIComponent(videoId)}/permanent`, 'DELETE'); }
  emptyRecycleBin() { return this.binRequest<{ removed: number }>('bin', 'DELETE'); }

  /** Creates the share link (or keeps the existing one). `expiresInDays`: 1 / 7 / 30, null = never, omitted = unchanged. */
  async shareVideo(videoId: string, expiresInDays?: number | null): Promise<ApiResponse<{ shareToken: string; shareExpiresAt: string | null }>> {
    if (!this.userEmail) {
      return {
        data: null,
        error: new Error('User not authenticated')
      };
    }
    
    // Ensure we're using the correct URL format and parameters
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/share`);
    
    // Make sure to include both userEmail and slug parameters
    url.searchParams.append('userEmail', this.userEmail);
    if (this.slug) {
      url.searchParams.append('slug', this.slug);
    }
    
    console.log('Sharing video with params:', {
      videoId,
      userEmail: this.userEmail,
      slug: this.slug,
      url: url.toString()
    });
    
    const response = await authFetch(url.toString(), {
      method: 'PUT',
      ...(expiresInDays !== undefined ? {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expiresInDays }),
      } : {}),
    });
    
    return handleResponse<{ shareToken: string; shareExpiresAt: string | null }>(response);
  }

  /** Turns the share link off (owner); sharing again creates a new link. */
  async unshareVideo(videoId: string): Promise<ApiResponse<null>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/share`);
    if (this.slug) url.searchParams.set('slug', this.slug);
    const response = await authFetch(url.toString(), { method: 'DELETE' });
    return handleResponse<null>(response);
  }

  async getSharedVideo(token: string): Promise<ApiResponse<Video>> {
    const url = new URL(`${API_BASE_URL}/api/videos/shared/${token}`);
    // Send viewer identity so the backend can apply visibility rules.
    if (this.slug)      url.searchParams.set('viewerSlug',  this.slug);
    if (this.userEmail) url.searchParams.set('viewerEmail', this.userEmail);
    const response = await authFetch(url.toString());
    return handleResponse<Video>(response);
  }

  async getEmojiReactionSummary(videoId: string): Promise<ApiResponse<{ emoji: string; count: number }[]>> {
    const response = await authFetch(`${API_BASE_URL}/api/videos/${videoId}/emoji-reactions`);
    return handleResponse<{ emoji: string; count: number }[]>(response);
  }

  /**
   * Signed-in viewers react by email (the backend takes it from the verified token); anyone
   * else by a random id kept in this browser. The id is always sent: the backend ignores it
   * for a verified viewer, and a viewer whose token turned out stale still reacts anonymously.
   */
  async addEmojiReaction(
    videoId: string,
    emoji: string
  ): Promise<ApiResponse<{ status: string; summary: { emoji: string; count: number }[] }>> {
    const body: Record<string, string | null> = {
      emoji,
      viewerEmail: this.userEmail,
      viewerName:  this.authorName ?? null,
      anonId:      getAnonViewerId(),
    };
    const response = await authFetch(`${API_BASE_URL}/api/videos/${videoId}/emoji-reaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return handleResponse<{ status: string; summary: { emoji: string; count: number }[] }>(response);
  }

  async patchShareVisibility(videoId: string, visibility: 'public' | 'org' | 'network' | 'private'): Promise<ApiResponse<Video>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/share-visibility`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visibility }),
    });
    return handleResponse<Video>(response);
  }

  /** Owner edits the title and/or description (HTML) of a saved video. */
  async updateVideoDetails(videoId: string, details: { title?: string; description?: string }): Promise<ApiResponse<Video>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/details`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(details),
    });
    return handleResponse<Video>(response);
  }

  /**
   * Starts an MP4 job on the server — Download as MP4 in `preset` quality, or Save as MP4
   * (`replace`: the MP4 becomes the video's file) — or returns the running job / ready MP4s.
   */
  async startVideoJob(
    videoId: string,
    type: 'mp4',
    options: { preset?: Mp4Preset; replace?: boolean } = {},
  ): Promise<ApiResponse<{ job: VideoJob | null; mp4s: VideoMp4s }>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/jobs`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, ...options }),
    });
    return handleResponse(response);
  }

  async getVideoJob(videoId: string): Promise<ApiResponse<{ job: VideoJob | null; mp4s: VideoMp4s }>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/jobs`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    return handleResponse(await authFetch(url.toString()));
  }

  /** Uploads a thumbnail image through the backend (the browser holds no storage credentials). */
  async uploadVideoThumbnail(videoId: string, thumbnail: Blob): Promise<ApiResponse<{ thumbnailUrl: string }>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/thumbnail`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const formData = new FormData();
    formData.append('thumbnail', thumbnail, 'thumbnail.jpg');
    const response = await authFetch(url.toString(), { method: 'POST', body: formData });
    return handleResponse<{ thumbnailUrl: string }>(response);
  }

  async updateVideoDuration(videoId: string, durationSeconds: number): Promise<ApiResponse<Video>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/duration`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ duration: Math.round(durationSeconds) }),
    });
    return handleResponse<Video>(response);
  }

  /** Probes the video duration server-side via ffprobe. Returns 0 if not yet determined. */
  async probeDuration(videoId: string): Promise<number> {
    if (!this.userEmail) return 0;
    try {
      const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/probe-duration`);
      addUserAuth(url, this.userEmail, this.slug || undefined);
      const response = await authFetch(url.toString());
      if (!response.ok) return 0;
      const data = await response.json() as { duration: number };
      return data.duration ?? 0;
    } catch (_) { return 0; }
  }

  async listSharedVideos(): Promise<ApiResponse<Video[]>> {
    if (!this.userEmail) {
      return { data: null, error: new Error('User not authenticated') };
    }
    const url = new URL(`${API_BASE_URL}/api/videos/shared-by-me`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString());
    return handleResponse<Video[]>(response);
  }

  /** The caller's deleted videos (deletion history), newest first. */
  /** One page of the deletion history, filtered by title, action and period (newest first). */
  async getDeletionHistory(query: DeletionHistoryQuery): Promise<ApiResponse<DeletionHistoryPage>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/deletion-history`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    url.searchParams.set('page', String(query.page));
    url.searchParams.set('pageSize', String(query.pageSize));
    if (query.q) url.searchParams.set('q', query.q);
    if (query.filter !== 'all') url.searchParams.set('filter', query.filter);
    if (query.days) url.searchParams.set('days', String(query.days));
    const response = await authFetch(url.toString());
    return handleResponse<DeletionHistoryPage>(response);
  }

  /**
   * Viewer insights: playback started or moved further (`position` = furthest second played).
   * `keepalive` lets the report finish when the page is closing.
   */
  async reportWatch(videoId: string, position: number, duration: number, keepalive = false, start = false): Promise<ApiResponse<{ counted: boolean }>> {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/videos/${videoId}/watch`, {
        method: 'POST',
        keepalive,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          position, duration, start,
          anonId: this.userEmail ? null : getAnonViewerId(),
          name: this.authorName ?? null,
        }),
      });
      return handleResponse<{ counted: boolean }>(response);
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /** The owner's viewer insights for a video. */
  async getVideoInsights(videoId: string): Promise<ApiResponse<VideoInsights>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/insights`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString());
    return handleResponse<VideoInsights>(response);
  }

  // Comment methods
  async listComments(videoId: string): Promise<ApiResponse<Comment[]>> {
    const response = await authFetch(`${API_BASE_URL}/api/videos/${videoId}/comments`);
    return handleResponse<Comment[]>(response);
  }

  async addComment(videoId: string, content: string, parentId?: string | null, timeSeconds?: number | null): Promise<ApiResponse<Comment>> {
    if (!this.userEmail) {
      return {
        data: null,
        error: new Error('User not authenticated')
      };
    }
    
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/comments`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    
    const response = await authFetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ content, parentId: parentId ?? null, timeSeconds: timeSeconds ?? undefined, authorName: this.authorName || undefined })
    });
    
    return handleResponse<Comment>(response);
  }

  async deleteComment(commentId: string): Promise<ApiResponse<null>> {
    if (!this.userEmail) {
      return {
        data: null,
        error: new Error('User not authenticated')
      };
    }
    
    const url = new URL(`${API_BASE_URL}/api/comments/${commentId}`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    
    const response = await authFetch(url.toString(), {
      method: 'DELETE'
    });
    
    return handleResponse<null>(response);
  }

  async deleteCommentAsOwner(commentId: string): Promise<ApiResponse<null>> {
    if (!this.userEmail) {
      return { data: null, error: new Error('User not authenticated') };
    }
    const url = new URL(`${API_BASE_URL}/api/comments/${commentId}/moderate`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), { method: 'DELETE' });
    return handleResponse<null>(response);
  }

  // Reaction methods
  async getReactions(videoId: string): Promise<ApiResponse<ReactionCounts>> {
    // Shared pages may load before the user context is set — fall back to the stored identity.
    // Anonymous viewers still get the real counts (the GET endpoint is public).
    if (!this.userEmail) {
      const userEmail = localStorage.getItem('userEmail');
      if (userEmail) this.setUserContext(userEmail, localStorage.getItem('slug') || undefined);
    }

    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/reactions`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    
    const response = await authFetch(url.toString());
    return handleResponse<ReactionCounts>(response);
  }

  async toggleReaction(videoId: string, reactionType: 'like' | 'dislike'): Promise<ApiResponse<ReactionCounts>> {
    // Shared pages may load before the user context is set — fall back to the stored identity.
    if (!this.userEmail) {
      const userEmail = localStorage.getItem('userEmail');
      if (userEmail) {
        this.setUserContext(userEmail, localStorage.getItem('slug') || undefined);
      } else {
        return {
          data: null,
          error: new Error('User not authenticated')
        };
      }
    }
    
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/reactions`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    
    const response = await authFetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ reactionType })
    });
    
    return handleResponse<ReactionCounts>(response);
  }

  // User management
  setUser(email: string, slug?: string, name?: string): void {
    this.userEmail = email;
    this.slug = slug || null;
    this.authorName = name || null;

    if (email) localStorage.setItem('userEmail', email);
    if (slug)  localStorage.setItem('slug', slug);

    console.log('[ApiClient] User context set:', { email: this.userEmail, slug: this.slug, name: this.authorName });
  }

  // Alias for setUser for better semantic clarity
  setUserContext(email: string, slug?: string, name?: string): void {
    this.setUser(email, slug, name);
  }

  getUser(): { email: string | null, slug: string | null } {
    return {
      email: this.userEmail,
      slug: this.slug
    };
  }

  isAuthenticated(): boolean {
    return this.userEmail !== null;
  }

  // Channel methods

  /** Returns all channels owned by the authenticated user */
  async getMyChannels(): Promise<ApiResponse<{ channels: Channel[]; maxChannels: number }>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/mine`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString());
    const raw = await handleResponse<any>(response);
    if (raw.error || !raw.data) return { data: null, error: raw.error };

    // Case 1: new backend envelope { channels: Channel[], maxChannels: number }
    if (!Array.isArray(raw.data) && raw.data.channels !== undefined) {
      const channels: Channel[] = Array.isArray(raw.data.channels)
        ? raw.data.channels
        : raw.data.channels ? [raw.data.channels] : [];
      return { data: { channels, maxChannels: raw.data.maxChannels ?? 5 }, error: null };
    }
    // Case 2: legacy multi-channel backend — returns Channel[]
    if (Array.isArray(raw.data)) {
      return { data: { channels: raw.data, maxChannels: 5 }, error: null };
    }
    // Case 3: legacy single-channel backend — returns a single Channel object
    if (raw.data && typeof raw.data === 'object' && raw.data.id) {
      return { data: { channels: [raw.data as Channel], maxChannels: 5 }, error: null };
    }
    return { data: { channels: [], maxChannels: 5 }, error: null };
  }

  /** @deprecated Use getMyChannels() — kept for compatibility */
  async getMyChannel(): Promise<ApiResponse<Channel>> {
    const result = await this.getMyChannels();
    if (result.data) return { data: result.data.channels[0] ?? null, error: null };
    return { data: null, error: result.error };
  }

  async createChannel(payload: {
    displayName: string;
    description?: string;
    type?: string;
    handle?: string;
    tags?: string[];
    subject?: string;
    level?: string;
    language?: string;
    category?: string;
  }): Promise<ApiResponse<Channel>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, slug: this.slug }),
    });
    return handleResponse<Channel>(response);
  }

  async updateChannel(channelId: string, updates: Partial<Channel>): Promise<ApiResponse<Channel>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    return handleResponse<Channel>(response);
  }

  async patchChannelVisibility(channelId: string, visibility: ChannelVisibility): Promise<ApiResponse<Channel>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/visibility`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visibility }),
    });
    return handleResponse<Channel>(response);
  }

  async regenerateChannelShareLink(channelId: string): Promise<ApiResponse<Channel>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/share-link`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), { method: 'POST' });
    return handleResponse<Channel>(response);
  }

  // `channelSlug` is the channel's own tenant; `slug` stays the viewer's tenant (the backend
  // uses it to decide same-tenant access), so the two are always sent separately.
  async getChannelByHandle(handle: string, channelSlug?: string): Promise<ApiResponse<Channel>> {
    const url = new URL(`${API_BASE_URL}/api/channels/${handle}`);
    if (this.slug) url.searchParams.set('slug', this.slug);
    if (this.userEmail) url.searchParams.set('userEmail', this.userEmail);
    url.searchParams.set('channelSlug', channelSlug || this.slug || '');
    const response = await authFetch(url.toString());
    return handleResponse<Channel>(response);
  }

  async getChannelByToken(shareToken: string, channelSlug?: string): Promise<ApiResponse<Channel>> {
    const url = new URL(`${API_BASE_URL}/api/channels/by-token/${shareToken}`);
    if (this.slug) url.searchParams.set('slug', this.slug);
    if (this.userEmail) url.searchParams.set('userEmail', this.userEmail);
    url.searchParams.set('channelSlug', channelSlug || this.slug || '');
    const response = await authFetch(url.toString());
    return handleResponse<Channel>(response);
  }

  async listOrgChannels(): Promise<ApiResponse<Channel[]>> {
    const url = new URL(`${API_BASE_URL}/api/channels`);
    if (this.slug) url.searchParams.set('slug', this.slug);
    if (this.userEmail) url.searchParams.set('userEmail', this.userEmail);
    const response = await authFetch(url.toString());
    return handleResponse<Channel[]>(response);
  }

  async deleteChannel(channelId: string): Promise<ApiResponse<null>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), { method: 'DELETE' });
    return handleResponse<null>(response);
  }

  async uploadChannelImage(channelId: string, file: File, type: 'avatar' | 'banner'): Promise<ApiResponse<Channel>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const formData = new FormData();
    formData.append('image', file);
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/upload-image`);
    url.searchParams.set('type', type);
    if (this.slug) url.searchParams.set('slug', this.slug);
    if (this.userEmail) url.searchParams.set('userEmail', this.userEmail);
    const response = await authFetch(url.toString(), { method: 'POST', body: formData });
    return handleResponse<Channel>(response);
  }

  /** Removes the channel's avatar or banner (owner only). */
  async removeChannelImage(channelId: string, type: 'avatar' | 'banner'): Promise<ApiResponse<Channel>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/image`);
    url.searchParams.set('type', type);
    if (this.slug) url.searchParams.set('slug', this.slug);
    const response = await authFetch(url.toString(), { method: 'DELETE' });
    return handleResponse<Channel>(response);
  }

  // ─── Channel member management ─────────────────────────────────────────────

  /** Works without a signed-in user for public channels. */
  async listChannelVideos(channelId: string, channelSlug?: string): Promise<ApiResponse<Video[]>> {
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/videos`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    url.searchParams.set('channelSlug', channelSlug || this.slug || '');
    const response = await authFetch(url.toString());
    return handleResponse<Video[]>(response);
  }

  async listChannelMembers(channelId: string): Promise<ApiResponse<{ email: string; addedAt: string }[]>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/members`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString());
    return handleResponse(response);
  }

  async addChannelMember(channelId: string, email: string): Promise<ApiResponse<{ email: string; addedAt: string }[]>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/members`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return handleResponse(response);
  }

  async removeChannelMember(channelId: string, email: string): Promise<ApiResponse<{ email: string; addedAt: string }[]>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    // Build the path first, then append auth as query params
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/members/${encodeURIComponent(email)}`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), { method: 'DELETE' });
    return handleResponse(response);
  }

  async assignVideoToChannel(videoId: string, channelId: string | null): Promise<ApiResponse<Video>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/videos/${videoId}/channel`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channelId }),
    });
    return handleResponse<Video>(response);
  }

  // Subscription methods
  // Subscriptions live in the channel's tenant — `channelSlug` is the channel's own slug.
  async subscribeChannel(channelId: string, channelSlug: string): Promise<ApiResponse<SubscriptionResult>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/subscribe`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    url.searchParams.set('channelSlug', channelSlug);
    const response = await authFetch(url.toString(), { method: 'POST' });
    return handleResponse<SubscriptionResult>(response);
  }

  async unsubscribeChannel(channelId: string, channelSlug: string): Promise<ApiResponse<SubscriptionResult>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/subscribe`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    url.searchParams.set('channelSlug', channelSlug);
    const response = await authFetch(url.toString(), { method: 'DELETE' });
    return handleResponse<SubscriptionResult>(response);
  }

  async checkSubscription(channelId: string, channelSlug: string): Promise<ApiResponse<{ subscribed: boolean }>> {
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/subscribe`);
    if (this.userEmail) addUserAuth(url, this.userEmail, this.slug || undefined);
    url.searchParams.set('channelSlug', channelSlug);
    const response = await authFetch(url.toString());
    return handleResponse<{ subscribed: boolean }>(response);
  }

  /** Channel owner only. */
  async listChannelSubscribers(channelId: string, channelSlug: string): Promise<ApiResponse<ChannelSubscriber[]>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/channels/${channelId}/subscribers`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    url.searchParams.set('channelSlug', channelSlug);
    const response = await authFetch(url.toString());
    return handleResponse<ChannelSubscriber[]>(response);
  }

  async listMySubscriptions(): Promise<ApiResponse<unknown[]>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const subUrl = new URL(`${API_BASE_URL}/api/subscriptions/mine`);
    addUserAuth(subUrl, this.userEmail, this.slug || undefined);
    const response = await authFetch(subUrl.toString());
    return handleResponse<unknown[]>(response);
  }

  async searchChannels(params: {
    q?: string; tags?: string; subject?: string; level?: string; lang?: string;
    // Phase 13-J: when set, only channels whose handle is in this list are returned.
    allowedHandles?: string[];
  }): Promise<ApiResponse<Channel[]>> {
    const url = new URL(`${API_BASE_URL}/api/channels/search`);
    if (this.slug) url.searchParams.set('slug', this.slug);
    if (this.userEmail) url.searchParams.set('userEmail', this.userEmail);
    if (params.q) url.searchParams.set('q', params.q);
    if (params.tags) url.searchParams.set('tags', params.tags);
    if (params.subject) url.searchParams.set('subject', params.subject);
    if (params.level) url.searchParams.set('level', params.level);
    if (params.lang) url.searchParams.set('lang', params.lang);
    if (params.allowedHandles?.length) {
      url.searchParams.set('allowedHandles', params.allowedHandles.join(','));
    }
    const response = await authFetch(url.toString());
    return handleResponse<Channel[]>(response);
  }

  // Playlist methods
  async listPlaylists(channelHandle: string, channelSlug?: string): Promise<ApiResponse<unknown[]>> {
    const url = new URL(`${API_BASE_URL}/api/channels/${channelHandle}/playlists`);
    if (this.slug) url.searchParams.set('slug', this.slug);
    if (this.userEmail) url.searchParams.set('userEmail', this.userEmail);
    url.searchParams.set('channelSlug', channelSlug || this.slug || '');
    const response = await authFetch(url.toString());
    return handleResponse<unknown[]>(response);
  }

  async createPlaylist(channelId: string, title: string, description?: string): Promise<ApiResponse<unknown>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/playlists`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channelId, title, description }),
    });
    return handleResponse<unknown>(response);
  }

  async setPlaylistVisibility(playlistId: string, channelId: string, isPublic: boolean): Promise<ApiResponse<unknown>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/playlists/${playlistId}`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channelId, isPublic }),
    });
    return handleResponse<unknown>(response);
  }

  async addVideoToPlaylist(playlistId: string, channelId: string, videoId: string): Promise<ApiResponse<unknown>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/playlists/${playlistId}/videos`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channelId, videoId }),
    });
    return handleResponse<unknown>(response);
  }

  async removeVideoFromPlaylist(playlistId: string, channelId: string, videoId: string): Promise<ApiResponse<unknown>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/playlists/${playlistId}/videos/${videoId}?channelId=${channelId}`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), { method: 'DELETE' });
    return handleResponse<unknown>(response);
  }

  async deletePlaylist(playlistId: string, channelId: string): Promise<ApiResponse<null>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/playlists/${playlistId}?channelId=${channelId}`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const response = await authFetch(url.toString(), { method: 'DELETE' });
    return handleResponse<null>(response);
  }

  /**
   * Upload an image attached to a comment.
   * Returns the Azure Blob public URL to embed in the rich-text HTML.
   * Naming on the server: comment-images/{videoId}_{authorHandle}_{ts}.{ext}
   */
  async uploadCommentImage(file: File, videoId: string): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('videoId', videoId);
    if (this.userEmail) formData.append('authorEmail', this.userEmail);
    if (this.slug)      formData.append('slug', this.slug);

    const url = new URL(`${API_BASE_URL}/api/comments/upload-image`);
    if (this.userEmail) addUserAuth(url, this.userEmail, this.slug || undefined);

    const response = await authFetch(url.toString(), { method: 'POST', body: formData });
    return handleResponse<{ url: string }>(response);
  }

  // ─── Whiteboard Collections integration ────────────────────────────────────

  /** Fetch this user's WB Collection videos from the proxy endpoint. */
  async getWbCollectionVideos(): Promise<ApiResponse<{ videos: Array<{
    name: string; url: string; createdAt: string; fileSize?: string; alreadyImported: boolean;
  }> }>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/library/whiteboard-collections`);
    url.searchParams.set('userEmail', this.userEmail);
    if (this.userId) url.searchParams.set('userId', this.userId);
    const res = await authFetch(url.toString(), {
      headers: { 'x-user-email': this.userEmail, 'x-user-id': this.userId || '' },
    });
    return handleResponse(res);
  }

  /** Import a WB video by reference (no blob copy). */
  async importFromWhiteboard(wbVideo: { name: string; url: string; createdAt: string; fileSize?: string }): Promise<ApiResponse<{ video: Video }>> {
    // The server identifies the caller from the sign-in token; no user id is needed here.
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/library/import-from-whiteboard`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const res = await authFetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wbVideo: { url: wbVideo.url } }),
    });
    return handleResponse(res);
  }

  /** Trigger server-side blob copy for an already-imported WB video (edit-copy flow). */
  async copyFromWhiteboard(videoId: string): Promise<ApiResponse<{ video: Video }>> {
    if (!this.userEmail) return { data: null, error: new Error('User not authenticated') };
    const url = new URL(`${API_BASE_URL}/api/library/copy-from-whiteboard`);
    addUserAuth(url, this.userEmail, this.slug || undefined);
    const res = await authFetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ videoId }),
    });
    return handleResponse(res);
  }

}

// Create and export a singleton instance
export const apiClient = new ApiClient();
